import { DestroyRef, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, combineLatest, Observable, Subscription, of } from 'rxjs';
import { URLConstants } from '../utility/url-constants';
import { BaseService } from 'src/app/core/services/base.httpService';
import {
  allowedKeys,
  LookupColumnsConfig,
  LookupDisplaySeparator,
  lookupHeaders,
  popupWidth,
  StatusCodes,
  countryCodes,
} from '../utility/constants';
import { AutocompleteConfig, IDropdownResponse } from '../models/dynamic.model';
import { AbstractControl, FormGroup, Validators, FormControl } from '@angular/forms';
import { ValidatorPattern } from '../utility/validators.pattern';
import { LookupPopupComponent } from '../components/lookup-popup/lookup-popup.component';
import { handleErrorMessage } from '../operators/error.operator';
import * as JsBarcode from 'jsbarcode';
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
  map,
  catchError,
  startWith,
  take,
} from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class CommonService {
  public checkOverlayOpened = new BehaviorSubject<boolean>(false);

  private readonly preventFurtherTypingSubscriptions = new WeakMap<
    AbstractControl,
    Subscription
  >();

  private readonly errorHandler = handleErrorMessage();

  constructor(private readonly baseService: BaseService) {}

  private formatCityDropdownLabel(
    stateCode: string | null | undefined,
    cityName: string | null | undefined
  ): string {
    const state = (stateCode ?? '').toString().trim();
    const name = (cityName ?? '').toString().trim();
    return state ? `${state}${LookupDisplaySeparator}${name}` : name;
  }

  /** Plain city name for API from free text or "STATE - City" label. */
  public parseCityNameForApi(value: string | undefined | null): string {
    if (!value) {
      return '';
    }
    const trimmed = value.trim();
    if (trimmed.includes(LookupDisplaySeparator)) {
      const parts = trimmed.split(LookupDisplaySeparator);
      return (
        parts.slice(1).join(LookupDisplaySeparator) || parts[0] || ''
      ).trim();
    }
    return trimmed;
  }

  /** Include a typed custom city in suggestions when it is not yet in the API list. */
  public mergeCustomCityIntoList(
    cities: IDropdownResponse[],
    typedCity: string | null | undefined
  ): IDropdownResponse[] {
    const cityName = this.parseCityNameForApi(typedCity);
    if (!cityName) {
      return cities;
    }
    const normalized = cityName.toLowerCase();
    const alreadyListed = cities.some((item) => {
      const label = item.cityName ?? item.city ?? item.name ?? '';
      return this.parseCityNameForApi(String(label)).toLowerCase() === normalized;
    });
    if (alreadyListed) {
      return cities;
    }
    return [
      {
        id: cityName,
        cityName,
        city: cityName,
        name: cityName,
        stateCode: '',
      },
      ...cities,
    ];
  }

  public isValidField(field: string | number) {
    return field !== '' && field !== null && field !== undefined;
  }

  public setOverlayOpen(data: any) {
    this.checkOverlayOpened.next(data);
  }

  public attachmentSignedUrl(data: any): Observable<any> {
    return this.baseService.initiateApiRequest(
      'PUT',
      URLConstants.signedurl,
      data
    );
  }

  public updateUserDetails(payload: any): Observable<any> {
    return this.baseService.initiateApiRequest(
      'POST',
      URLConstants.updateUserDetails,
      payload,
      '',
      'json'
    );
  }

  public attachmentStoredSignedUrl(data: any, fileName: any): Observable<any> {
    return this.baseService.uploadFile(data.url, fileName);
  }

  public attachmentFullManSignedUrl(data: any): Observable<any> {
    return this.baseService.initiateApiRequest(
      'POST',
      URLConstants.signedManifest,
      data
    );
  }

  public shareAirwayBillViaEmail(
    payload: any,
    airwayBillNumber: number
  ): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        'POST',
        URLConstants.shareViaEmail + airwayBillNumber,
        payload,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public setControlRequiredValidation(form: FormGroup, controlName: string) {
    const control = form.get(controlName);
    if (control) {
      control.addValidators(Validators.required);
      control.updateValueAndValidity();
    }
  }

  public removeControlRequiredValidation(form: FormGroup, controlName: string) {
    const control = form.get(controlName);
    if (control) {
      control.removeValidators(Validators.required);
      control.updateValueAndValidity();
    }
  }

  public clearControlValidators(form: FormGroup, controlName: string) {
    const control = form.get(controlName);
    if (control) {
      control.clearValidators();
      control.updateValueAndValidity();
    }
  }

  public markControlUnTouched(form: any, controlName: string) {
    const formName = form;
    formName.get(controlName)?.markAsUntouched();
  }

  public markControlTouched(form: any, controlName: string) {
    const formName = form;
    formName.get(controlName)?.markAsTouched();
  }

  /* For getting width of text fields to show tooltip */
  public getControlWidth(
    event: MouseEvent,
    reduceWidth?: number,
    isFrom?: string
  ) {
    const target = event.target as HTMLElement;
    const parentElement =
      target.parentElement?.parentNode?.parentNode?.parentNode?.parentElement;
    if (reduceWidth) {
      return (parentElement?.offsetWidth ?? 0) - reduceWidth + 'px';
    } else {
      return (
        (isFrom === 'airway-bill-piece' ? 337 : parentElement?.offsetWidth) +
        'px'
      );
    }
  }

  /* For setting width, event and enabling of tooltips for text fields */
  public setErrorTooltipData(
    event: MouseEvent,
    fieldName: string,
    setWidth: any,
    setTooltipEvent: any,
    setErrorTooltip: any,
    reduceWidth?: number,
    isFrom?: string
  ) {
    setWidth[fieldName] = this.getControlWidth(event, reduceWidth, isFrom);
    setTooltipEvent[fieldName] = event;
    setErrorTooltip[fieldName] = true;
  }

  /* For clearing of tooltips for text fields */
  public closeErrorTooltip(controlName: string, setErrorTooltip: any) {
    setErrorTooltip[controlName] = false;
  }

  public setPhoneControlValidation(
    form: any,
    controlName: string,
    isSpecificFormat: boolean
  ) {
    const formName = form;
    formName.get(controlName)?.updateValueAndValidity();
  }

  public preventTypingOrPasting(event: KeyboardEvent | ClipboardEvent): void {
    if (event instanceof KeyboardEvent) {
      if (!allowedKeys.includes(event.key)) {
        event.preventDefault();
      }
    } else {
      event.preventDefault();
    }
  }

  public getAutocompleteDropdownName(config: AutocompleteConfig) {
    const { dropdownResponse, formName, controlName, apiControlName, param, destroyRef } =
      config;
    if (!Array.isArray(dropdownResponse) || dropdownResponse.length === 0)
      return; // Check if dropdownResponse is an array
    let controlValue: string | null = formName.get(controlName)?.value;
    if (!controlValue) {
      formName.get(apiControlName)?.setValue(''); // No need to process if the control value is empty
    } else {
      controlValue = controlValue.replace(/\s/g, ''); // Remove spaces
      const { exactMatch, potentialMatches } = this.findMatches(
        dropdownResponse,
        controlValue,
        param as keyof IDropdownResponse,
        controlName
      );

      if (exactMatch) {
        this.handleExactMatch(
          formName,
          exactMatch,
          controlName,
          apiControlName,
          param,
          destroyRef
        );
      }
      // Partial matches are never auto-selected; only an exact match populates the party.
    }
  }

  public getAutocompleteDropdownBinding(
    dropdownResponse: IDropdownResponse[],
    formName: FormGroup,
    controlName: string,
    apiControlName: string,
    param: keyof IDropdownResponse
  ) {
    const control = formName.get(controlName);
    const apiControl = formName.get(apiControlName);

    if (!control || !apiControl) return;

    const matchingData = dropdownResponse.find(
      item => item[param] === control.value
    );

    if (matchingData) {
      apiControl.setValue(matchingData.id);
      this.clearErrors(formName, controlName);
    } else {
      control.setErrors({ ...control.errors, autocompleteError: true });
    }
  }

  public openLookupPopup(
    modalService: any,
    controlName: string,
    lookupData: IDropdownResponse[],
    selectedOption: string
  ) {
    const lookupColumns = LookupColumnsConfig.filter(
      columnsData => columnsData.label === this.getLookUpHeaderName(controlName)
    );
    const lookupDialogRef = modalService.openPopup(
      {
        showModalCloseOption: true,
        displayedColumnsList: [
          ...lookupColumns.map(column => column.key),
          'action',
        ],
        tableColumns: lookupColumns,
        tableData:
          controlName === 'countryCode' ||
          controlName === 'state' ||
          controlName === 'city'
            ? this.getLookupData(controlName, lookupData)
            : lookupData,
        selectedOptionData: selectedOption,
      },
      this.getLookUpHeaderName(controlName),
      LookupPopupComponent,
      popupWidth.searchPopup
    );
    return lookupDialogRef;
  }

  public getLookupData(controlName: string, lookupData: IDropdownResponse[]) {
    if (controlName === 'countryCode') {
      return lookupData.map(data => {
        return {
          ...data,
          country: data.id + ' - ' + data.name,
        };
      });
    } else if (controlName === 'state') {
      return lookupData.map(data => {
        return {
          ...data,
          state: data.stateCode + ' - ' + data.name,
        };
      });
    } else if (controlName === 'city') {
      return lookupData.map(data => {
        const cityRecord = data as IDropdownResponse & {
          city_name?: string;
          state_code?: string;
        };
        const cityName =
          cityRecord.cityName ?? cityRecord.city_name ?? cityRecord.name ?? '';
        const stateCode =
          cityRecord.stateCode ?? cityRecord.state_code ?? '';
        return {
          ...data,
          cityName,
          stateCode,
          city: this.formatCityDropdownLabel(stateCode, cityName),
        };
      });
    } else {
      return lookupData;
    }
  }

  public getBarcodeImage(extData: string) {
    const canvas = document.createElement('canvas');
    JsBarcode(canvas, extData, {
      text: extData,
      width: 1.8,
      height: 30,
      format: 'CODE39',
      margin: 3,
      textMargin: 2,
      fontSize: 18,
    });
    return canvas.toDataURL('image/jpeg');
  }

  public generateBarcode(element: any, extData: string) {
    const mmFactor = this.calcPx2MmFactor();
    JsBarcode(element, extData, {
      text: extData,
      width: 1.8,
      height: 10 * mmFactor,
      format: 'CODE39',
      margin: 3 * mmFactor, // 5mm
      textMargin: 2 * mmFactor, // 2mm
      fontSize: 18,
    });
  }

  public setMeasurementOptionsValues() {
    const userDetails = JSON.parse(localStorage.getItem('userDetails') ?? 'null');
    return {
      weightValue: userDetails.measurementOptions.split('/')[0],
      sizeValue: userDetails.measurementOptions.split('/')[1],
      weightUnitValue:
        userDetails.measurementOptions.split('/')[0] === 'Pounds'
          ? 'lbs'
          : 'kgs',
      sizeUnitValue:
        userDetails.measurementOptions.split('/')[1] === 'Inches'
          ? 'inch'
          : 'cms',
    };
  }

  private calcPx2MmFactor() {
    let e = document.createElement('div');
    e.style.position = 'absolute';
    e.style.width = '100mm';
    document.body.appendChild(e);
    let rect = e.getBoundingClientRect();
    document.body.removeChild(e);
    return rect.width / 100;
  }

  private getLookUpHeaderName(controlName: string) {
    if (controlName === 'countryCode') {
      return lookupHeaders.country;
    } else if (controlName === 'state') {
      return lookupHeaders.state;
    } else if (controlName === 'city') {
      return lookupHeaders.city;
    } else {
      return '';
    }
  }

  private findMatches(
    dropdownResponse: IDropdownResponse[],
    controlValue: string,
    param: keyof IDropdownResponse,
    controlName: string
  ) {
    let exactMatch: IDropdownResponse | null = null;
    const potentialMatches: IDropdownResponse[] = [];

    dropdownResponse.forEach((item: IDropdownResponse) => {
      const dropdownOptionValue = item[param];
      if (typeof dropdownOptionValue !== 'string') return;
      const dropdownOption = dropdownOptionValue.replace(/\s/g, ''); // Remove spaces
      if (controlName === 'name') {
        const normalizedControlValue = controlValue
          .replace(/^@+/, '')
          .toLowerCase();
        const nameOption = item['name'].replace(/\s/g, ''); // Remove spaces
        const displayIdOption = item['displayId']?.replace(/\s/g, ''); // Remove spaces
        if (
          nameOption.toLowerCase() === normalizedControlValue ||
          displayIdOption?.toLowerCase() === normalizedControlValue
        ) {
          exactMatch = item;
        }

        if (
          nameOption.toLowerCase().includes(normalizedControlValue) ||
          displayIdOption?.toLowerCase().includes(normalizedControlValue)
        ) {
          potentialMatches.push(item);
        }
      } else {
        if (dropdownOption.toLowerCase() === controlValue.toLowerCase()) {
          exactMatch = item;
        }

        if (dropdownOption.toLowerCase().includes(controlValue.toLowerCase())) {
          potentialMatches.push(item);
        }
      }
    });

    return { exactMatch, potentialMatches };
  }

  private handleExactMatch(
    form: FormGroup,
    exactMatch: any,
    controlName: string,
    apiControlName: string,
    param: string,
    destroyRef?: DestroyRef
  ) {
    form.get(apiControlName)?.setValue(exactMatch.id);
    form.get(controlName)?.patchValue(exactMatch[param]);
    this.clearErrors(form, controlName);
    this.preventFurtherTyping(form, controlName, exactMatch[param], destroyRef);
  }

  private clearErrors(form: FormGroup, controlName: string) {
    form.get(controlName)?.setErrors({ autocompleteError: false });
    delete form.get(controlName)?.errors?.['autocompleteError'];
    const controlErrors = form.get(controlName)?.errors;
    if (controlErrors && Object.keys(controlErrors).length === 0) {
      form.get(controlName)?.setErrors(null);
    }
  }

  private preventFurtherTyping(
    form: FormGroup,
    controlName: string,
    exactMatchName: string,
    destroyRef?: DestroyRef
  ) {
    const control = form.get(controlName);
    if (!control) {
      return;
    }

    this.clearPreventFurtherTypingSubscription(control);

    const valueChanges$ = destroyRef
      ? control.valueChanges.pipe(takeUntilDestroyed(destroyRef))
      : control.valueChanges;

    const subscription = valueChanges$.subscribe(value => {
      if (value !== exactMatchName) {
        const lastCharacter = value?.slice(-1);
        const allowedKeys = ['Backspace', 'Delete'];
        if (lastCharacter && !allowedKeys.includes(lastCharacter)) {
          control.setValue(exactMatchName, { emitEvent: false });
        } else if (value === '') {
          this.clearPreventFurtherTypingSubscription(control);
        }
      }
    });

    this.preventFurtherTypingSubscriptions.set(control, subscription);
  }

  private clearPreventFurtherTypingSubscription(
    control: AbstractControl
  ): void {
    const existing = this.preventFurtherTypingSubscriptions.get(control);
    if (existing) {
      existing.unsubscribe();
      this.preventFurtherTypingSubscriptions.delete(control);
    }
  }

  public getCityDetails(data: any): Observable<any> {
    const url = URLConstants.getCityDetails + data;
    return this.baseService.initiateApiRequest('GET', url, data, 'json');
  }

  public getCityAutocompleteObservable(
    cityControl: FormControl
  ): Observable<IDropdownResponse[]> {
    return cityControl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((cityInput: string): Observable<IDropdownResponse[]> => {
        if (!cityInput || typeof cityInput !== 'string' || cityInput.trim().length < 3) {
          return of([] as IDropdownResponse[]);
        }
        // Call API with city name using getCityDetails
        return this.getCityDetails(cityInput.trim()).pipe(
          map((response: any): IDropdownResponse[] => {
            if (response.statusCode === StatusCodes.Success && response.data.length > 0) {
              // Transform API response to match IDropdownResponse format
              return response.data.map((item: any) => {
                const cityLabel = this.formatCityDropdownLabel(
                  item.stateCode,
                  item.cityName ?? item.city
                );
                return {
                  ...item,
                  id: item.id ?? item.cityCode ?? cityLabel,
                  city: cityLabel,
                  state: item.state ?? '',
                  country: item.country ?? '',
                  name: cityLabel,
                };
              }) as IDropdownResponse[];
            }
            return [] as IDropdownResponse[];
          }),
          catchError(() => of([] as IDropdownResponse[]))
        );
      })
    );
  }

  /** Country-scoped city suggestions; input stays free text (no forced selection). */
  public getCountryCityTypeaheadObservable(
    cityControl: FormControl,
    cityList$: Observable<IDropdownResponse[]>
  ): Observable<IDropdownResponse[]> {
    return combineLatest([
      cityControl.valueChanges.pipe(startWith(cityControl.value ?? '')),
      cityList$,
    ]).pipe(
      map(([val, cityList]) => {
        const list = cityList ?? [];
        const filterValue =
          typeof val === 'string'
            ? val.replaceAll(/[\s-]/g, '').toLowerCase()
            : '';
        if (!filterValue) {
          return list;
        }
        return list.filter((item) =>
          (item.city ?? item.name ?? '')
            .replaceAll(/[\s-]/g, '')
            .toLowerCase()
            .includes(filterValue)
        );
      })
    );
  }

  private findMatchingCountry(
    countryName: string,
    countryList: IDropdownResponse[]
  ): IDropdownResponse | undefined {
    const apiCountryName = countryName?.trim().toLowerCase();
    return (
      countryList.find(
        (country: IDropdownResponse) =>
          country.name?.trim().toLowerCase() === apiCountryName
      ) ??
      countryList.find((country: IDropdownResponse) => {
        const name = country.name?.trim().toLowerCase();
        return name && apiCountryName
          ? name.includes(apiCountryName) || apiCountryName.includes(name)
          : false;
      })
    );
  }

  private findMatchingState(
    stateName: string,
    stateList: IDropdownResponse[]
  ): IDropdownResponse | undefined {
    if (!stateName || !stateList?.length) return undefined;
    
    const apiStateName = stateName.trim();
    
    const stateCodeRegex = ValidatorPattern.stateCodeWithDash;
    const stateCodeMatch = stateCodeRegex.exec(apiStateName);
    const extractedCode = stateCodeMatch ? stateCodeMatch[1].toUpperCase() : null;
    const extractedName = stateCodeMatch 
      ? apiStateName.substring(stateCodeMatch[0].length).trim() 
      : apiStateName;
    
    const apiStateNameLower = extractedName.toLowerCase();
    
    if (extractedCode) {
      const codeMatch = stateList.find(
        (state: IDropdownResponse) =>
          state.stateCode?.trim().toUpperCase() === extractedCode
      );
      if (codeMatch) return codeMatch;
    }
    
    const exactNameMatch = stateList.find(
      (state: IDropdownResponse) =>
        state.name?.trim().toLowerCase() === apiStateNameLower
    );
    if (exactNameMatch) return exactNameMatch;
    
    if (apiStateName.length === 2 && ValidatorPattern.twoLetterCode.test(apiStateName)) {
      const codeOnlyMatch = stateList.find(
        (state: IDropdownResponse) =>
          state.stateCode?.trim().toUpperCase() === apiStateName.toUpperCase()
      );
      if (codeOnlyMatch) return codeOnlyMatch;
    }
    
    const startsWithMatch = stateList.find((state: IDropdownResponse) => {
      const name = state.name?.trim().toLowerCase();
      return name?.startsWith(apiStateNameLower);
    });
    if (startsWithMatch) return startsWithMatch;
    
    return stateList.find((state: IDropdownResponse) => {
      const name = state.name?.trim().toLowerCase();
      return name && apiStateNameLower
        ? name.includes(apiStateNameLower) || apiStateNameLower.includes(name)
        : false;
    });
  }

  private setStateValue(
    form: FormGroup,
    selectedCity: IDropdownResponse,
    stateList?: IDropdownResponse[]
  ): void {
    if (!selectedCity.state) return;

    const stateControl = form.get('state');
    if (!stateControl) return;

    if (stateList && stateList.length > 0) {
      const matchingState = this.findMatchingState(selectedCity.state, stateList);
      if (matchingState?.stateCode) {
        stateControl.setValue(
          `${matchingState.stateCode} - ${matchingState.name}`,
          { emitEvent: false }
        );
        return;
      }
    }
    stateControl.setValue(selectedCity.state, { emitEvent: false });
  }

  private setCountryValue(
    form: FormGroup,
    selectedCity: IDropdownResponse,
    countryList?: IDropdownResponse[]
  ): void {
    if (!selectedCity.country) return;

    const countryCodeControl = form.get('countryCode');
    const countryControl = form.get('country');
    if (!countryCodeControl) return;

    if (countryList && countryList.length > 0) {
      const matchingCountry = this.findMatchingCountry(
        selectedCity.country,
        countryList
      );
      if (matchingCountry?.id) {
        countryCodeControl.setValue(
          `${matchingCountry.id} - ${matchingCountry.name}`,
          { emitEvent: false }
        );
        countryControl?.setValue(matchingCountry.name, { emitEvent: false });
        return;
      }
    }
    countryCodeControl.setValue(selectedCity.country, { emitEvent: false });
  }

  private clearCityErrors(form: FormGroup): void {
    const cityControl = form.get('city');
    if (!cityControl) return;

    cityControl.setErrors({ autocompleteError: false });
    delete cityControl.errors?.['autocompleteError'];
    const controlErrors = cityControl.errors;
    if (controlErrors && Object.keys(controlErrors).length === 0) {
      cityControl.setErrors(null);
    }
  }

  public handleCitySelection(
    selectedCity: IDropdownResponse,
    form: FormGroup,
    countryList?: IDropdownResponse[],
    stateList?: IDropdownResponse[]
  ): void {
    if (!selectedCity || !form) return;

    const cityControl = form.get('city');
    cityControl?.setValue(selectedCity.city ?? selectedCity.name, {
      emitEvent: false,
    });

    this.setStateValue(form, selectedCity, stateList);
    this.setCountryValue(form, selectedCity, countryList);
    this.clearCityErrors(form);
  }

  private setFallbackValues(
    form: FormGroup,
    selectedCity: IDropdownResponse
  ): void {
    const countryCodeControl = form.get('countryCode');
    const stateControl = form.get('state');

    countryCodeControl?.setValue(selectedCity.country ?? '', { emitEvent: false });
    if (selectedCity.state) {
      stateControl?.setValue(selectedCity.state, { emitEvent: false });
    }
  }

  private handleStateListSuccess(
    form: FormGroup,
    selectedCity: IDropdownResponse,
    stateList: IDropdownResponse[],
    onStateListUpdated: (stateList: IDropdownResponse[]) => void
  ): void {
    onStateListUpdated(stateList);
    this.setStateValue(form, selectedCity, stateList);
    this.clearCityErrors(form);
  }

  private handleStateListError(
    form: FormGroup,
    selectedCity: IDropdownResponse
  ): void {
    if (selectedCity.state) {
      const stateControl = form.get('state');
      stateControl?.setValue(selectedCity.state, { emitEvent: false });
    }
  }

  private processStateDataResponse(
    form: FormGroup,
    selectedCity: IDropdownResponse,
    data: any,
    onStateListUpdated: (stateList: IDropdownResponse[]) => void
  ): void {
    if (data.code == StatusCodes.Success) {
      const stateList = data?.data ?? [];
      this.handleStateListSuccess(form, selectedCity, stateList, onStateListUpdated);
      return;
    }
    this.handleStateListError(form, selectedCity);
  }

  private fetchAndProcessStateData(
    form: FormGroup,
    selectedCity: IDropdownResponse,
    countryId: any,
    getStateDataObservable: (countryId: any) => Observable<any>,
    onStateListUpdated: (stateList: IDropdownResponse[]) => void,
    onError?: (error: any) => void
  ): void {
    getStateDataObservable(countryId).pipe(take(1)).subscribe({
      next: (data: any) => {
        this.processStateDataResponse(form, selectedCity, data, onStateListUpdated);
      },
      error: (error: any) => {
        this.handleStateListError(form, selectedCity);
        onError?.(error);
      },
    });
  }

  private setCountryAndFetchStates(
    form: FormGroup,
    selectedCity: IDropdownResponse,
    matchingCountry: IDropdownResponse,
    getStateDataObservable: (countryId: any) => Observable<any>,
    onStateListUpdated: (stateList: IDropdownResponse[]) => void,
    onError?: (error: any) => void
  ): void {
    const countryCodeControl = form.get('countryCode');
    const countryControl = form.get('country');

    const countryCodeValue = `${matchingCountry.id} - ${matchingCountry.name}`;
    countryCodeControl?.setValue(countryCodeValue, { emitEvent: false });
    countryControl?.setValue(matchingCountry.name, { emitEvent: false });

    this.fetchAndProcessStateData(
      form,
      selectedCity,
      matchingCountry.id,
      getStateDataObservable,
      onStateListUpdated,
      onError
    );
  }

  public handleCitySelectionWithStateFetch(
    selectedCity: IDropdownResponse,
    form: FormGroup,
    countryList: IDropdownResponse[],
    getStateDataObservable: (countryId: any) => Observable<any>,
    onStateListUpdated: (stateList: IDropdownResponse[]) => void,
    onError?: (error: any) => void
  ): void {
    if (!selectedCity || !form) return;

    const cityControl = form.get('city');
    cityControl?.setValue(selectedCity.city ?? selectedCity.name, {
      emitEvent: false,
    });

    if (!selectedCity.country || !countryList?.length) {
      this.setFallbackValues(form, selectedCity);
      return;
    }

    const matchingCountry = this.findMatchingCountry(
      selectedCity.country,
      countryList
    );

    if (matchingCountry?.id) {
      this.setCountryAndFetchStates(
        form,
        selectedCity,
        matchingCountry,
        getStateDataObservable,
        onStateListUpdated,
        onError
      );
    } else {
      this.setFallbackValues(form, selectedCity);
    }
  }

  public removeControlsValidationCountryBased(
    form: FormGroup,
    countryCodeVal?: string,
    includeTaxId?: boolean
  ): void {
    const countryCode = form.get('countryCode')?.value?.split(' - ')[0];
    if (
      countryCode !== countryCodes.usa &&
      countryCode !== countryCodes.canada &&
      countryCode !== countryCodes.brazil
    ) {
      this.clearControlValidators(form, 'state');
      if (includeTaxId && countryCodeVal === countryCodes.brazil) {
        this.clearControlValidators(form, 'taxId');
      }
      form.get('zipCode')?.removeValidators(Validators.required);
      form
        .get('zipCode')
        ?.setValidators([Validators.minLength(4), Validators.maxLength(10)]);
      form.get('zipCode')?.updateValueAndValidity();
    } else {
      form
        .get('zipCode')
        ?.setValidators([Validators.minLength(4), Validators.maxLength(10)]);
      form.get('zipCode')?.updateValueAndValidity();
    }
  }
}
